import { Children, isValidElement, useContext, useEffect } from 'react';

import type {
  HeadElement,
  HeadElementKind,
} from '@xing-com/crate-xinglet/internal';

import { HelmetContext } from './context';

export function isSelfClosingElement(kind: HeadElementKind): boolean {
  switch (kind) {
    case 'noscript':
    case 'script':
    case 'style':
    case 'title':
      return false;
    default:
      return true;
  }
}

export function isHeadElementKind(kind: string): kind is HeadElementKind {
  switch (kind) {
    case 'base':
    case 'body':
    case 'head':
    case 'html':
    case 'link':
    case 'meta':
    case 'noscript':
    case 'script':
    case 'style':
    case 'title':
      return true;
    default:
      return false;
  }
}

export function extractHeadElements({
  children,
  htmlAttributes,
  titleTemplate,
}: React.PropsWithChildren<HelmetProps>): HeadElement[] {
  const elements = new Array<HeadElement>();

  if (htmlAttributes) {
    elements.push({ kind: 'html', attributes: htmlAttributes });
  }

  if (titleTemplate) {
    elements.push({ kind: 'title', attributes: { titleTemplate } });
  }

  for (const child of Children.toArray(children)) {
    if (!isValidElement<Record<string, unknown>>(child)) continue;
    if (!child || typeof child !== 'object') continue;
    if (!('type' in child) || typeof child.type !== 'string') continue;
    if (!isHeadElementKind(child.type)) continue;

    const kind = child.type;
    const element: HeadElement = {
      kind,
      content: undefined,
      attributes: {},
    };

    for (const [key, value] of Object.entries(child.props)) {
      const text = Array.isArray(value) ? value.join('') : String(value);

      if (key === 'children') {
        if (isSelfClosingElement(kind)) {
          throw new Error(
            `<${kind} /> elements are self-closing and can not contain children. Refer to our API for more information.`
          );
        }

        element.content = text;
      } else {
        element.attributes[key] = text;
      }
    }

    elements.push(element);
  }

  return elements;
}

type HelmetProps = React.PropsWithChildren<{
  htmlAttributes?: Record<string, string>;
  titleTemplate?: string;
}>;

const isServer = typeof window === 'undefined';

export const Helmet: React.FC<HelmetProps> = (props) => {
  const ctx = useContext(HelmetContext);
  const elements = extractHeadElements(props);

  if (isServer) {
    ctx.addElements?.(elements);
  }

  useEffect(() => {
    ctx.addElements?.(elements);
    return () => ctx.removeElements?.(elements);
  });

  return null;
};
